import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Property } from '@/types/property';
import { Link } from '@tanstack/react-router';
import { ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { ContactSellerForm } from '../ContactSellerForm';
import { useBookingQueries } from '@/hooks/queries/useBookingQueries';

interface ContactCardProps {
  property: Property;
}

export default function SellerInformationCard({ property }: ContactCardProps) {
  const [contactFormOpen, setContactFormOpen] = useState(false);
  const isSold = property.status === "archive";
  const hasExternalUrl = Boolean(property.external_url);

  // Determine card title based on listing state
  const cardTitle = hasExternalUrl
    ? "External Listing"
    : "Contact Seller";

  // Determine card description based on listing state
  const cardDescription = isSold
    ? "This property has been sold and is no longer available."
    : hasExternalUrl
      ? "This property is listed on an external site. Click below to view the full listing."
      : "Send an inquiry to the seller about this property";

  const { useFetchBookings } = useBookingQueries();
  const hasBookings = useFetchBookings(property.property_id).data?.length > 0;
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{cardTitle}</CardTitle>
          <CardDescription>{cardDescription}</CardDescription>
        </CardHeader>

        {isSold ? (
          <CardContent className="text-center py-6">
            <p>This property has been sold and is no longer available.</p>
          </CardContent>
        ) : hasExternalUrl ? (
          <>
            <CardFooter>
              <Button
                className="w-full"
                onClick={() => window.open(property.external_url, '_blank')}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                View External Listing
              </Button>
            </CardFooter>
          </>
        ) : (
          <CardFooter className="flex gap-3">
            <Button
              className="w-full"
              onClick={() => setContactFormOpen(true)}
            >
              Contact Seller
            </Button>
            {hasBookings && (
              <Button className="w-full" asChild>
                <Link to="/bookings/create/$propertyId" params={{ propertyId: property.property_id }}>
                  Book a Tour
                </Link>
              </Button>
            )}
          </CardFooter>
        )}
      </Card>
      {!isSold && !hasExternalUrl && (
        <ContactSellerForm
          property={property}
          open={contactFormOpen}
          setOpen={setContactFormOpen}
        />
      )}
    </>
  );
}
